import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falClock } from 'shared/@generated/icons/falClock';
import { falLocationDot } from 'shared/@generated/icons/falLocationDot';
import { falPaw } from 'shared/@generated/icons/falPaw';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, Breeder, MediaAsset } from 'shared/@types';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { Button } from 'shared/components/button';
import ContactBreederButton from 'shared/components/contact-breeder-button';
import { Icon, type FaIcon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import LikeButton from 'shared/components/like-button';
import { Text } from 'shared/components/text';
import UserCard from 'shared/components/user-card';
import { getBreederAffiliation } from 'shared/library/breeder-affiliation';
import { getBreederGalleryAssets } from 'shared/library/get-model-image';
import { useIsCurrentRoute, useRoute } from 'shared/library/routes/route';

interface BreederProfileHeaderProps {
  breeder: Breeder;
  mediaAssets?: MediaAsset[];
}

export function BreederProfileHeader({ breeder, mediaAssets }: BreederProfileHeaderProps) {
  const { t } = useTranslation('screens');
  const { t: tWeb } = useTranslation('web');
  const route = useRoute();
  const owners = breeder.owners ?? [];
  const users = breeder.users ?? [];
  const breeds = breeder.breeds ?? [];
  const owner = owners[0] ?? users[0] ?? null;
  const profileUsers = owners.length > 0 ? owners : users;
  const breederCity = owner?.city ?? '';
  const breederAffiliation = getBreederAffiliation(breeder.affiliation);
  const galleryAssets = mediaAssets?.length ? mediaAssets : getBreederGalleryAssets(breeder);
  const images = getBreederProfileImages(galleryAssets);
  const thumbnailImages = getBreederProfileThumbnailImages(images);
  const breederResponseTimeText = getBreederResponseTimeText(
    breeder.average_response_time_minutes,
    t,
  );
  const breederBreedsLabel =
    breeds.length > 0
      ? breeds.map((breed) => breed.name).join(', ')
      : t('breeders.show_breeder.no_breeds_added');

  return (
    <View
      className={
        'md:border-brand-brown-dark/10 my-3 rounded-lg border-transparent bg-transparent md:my-6 md:border md:bg-white'
      }
    >
      <View className={'gap-3 md:p-6 lg:flex-row-reverse lg:items-stretch lg:gap-10 xl:gap-14'}>
        <View
          className={
            'relative -mx-3 overflow-hidden rounded-none bg-white md:mx-0 md:rounded-lg lg:aspect-video lg:flex-1 lg:self-start'
          }
        >
          <View className={'gap-1.5 lg:hidden'}>
            <Image
              src={images[0]}
              alt={breeder.name}
              containerClassName={'aspect-4/3 w-full'}
              imageClassName={'object-cover'}
              sizeType={'large'}
              aspectRatio={4 / 3}
              priority={true}
            />

            <View className={'flex-row gap-1.5'}>
              {thumbnailImages.map((image, index) => (
                <Image
                  key={index}
                  src={image}
                  alt={breeder.name}
                  containerClassName={'aspect-square min-w-0 flex-1'}
                  imageClassName={'object-cover'}
                  sizeType={'card'}
                  aspectRatio={1}
                />
              ))}
            </View>
          </View>

          <View className={'absolute inset-0 hidden gap-1.5 lg:flex lg:flex-row'}>
            <Image
              src={images[0]}
              alt={breeder.name}
              containerClassName={'h-full w-3/4'}
              imageClassName={'object-cover'}
              sizeType={'large'}
              aspectRatio={4 / 3}
              priority={true}
            />

            <View className={'min-h-0 flex-1 gap-1.5 self-stretch'}>
              {thumbnailImages.map((image, index) => (
                <Image
                  key={index}
                  src={image}
                  alt={breeder.name}
                  containerClassName={'min-h-0 w-full flex-1'}
                  imageClassName={'object-cover'}
                  sizeType={'card'}
                  aspectRatio={4 / 3}
                />
              ))}
            </View>
          </View>

          {galleryAssets.length > 0 && (
            <View className={'absolute bottom-3 right-3 z-10'}>
              <Button
                testID={'breeder-profile-photos'}
                href={route('breeders.show.photos', { breeder: breeder.slug })}
                text={t('breeders.show_breeder.view_all_photos')}
                icon={falAngleRight}
                iconPosition={'right'}
                color={'white'}
                dense={true}
                textClassName={'whitespace-nowrap'}
              />
            </View>
          )}
        </View>

        <View className={'min-w-0 lg:w-[38%] xl:w-[36%]'}>
          <Breadcrumb
            className={'mb-1.5'}
            items={[
              { text: tWeb('layout.menu.breeders'), href: route('breeders.index') },
              { text: breeder.name, href: null },
            ]}
          />

          <Text
            accessibilityRole={'header'}
            aria-level={1}
            className={
              'text-brand-brown-dark max-w-full break-words text-3xl font-black leading-tight md:text-4xl xl:text-5xl'
            }
          >
            {breeder.name}
          </Text>

          <Text className={'text-brand-brown-dark/70 mt-2'}>
            {t(`breeders.show_breeder.affiliation_label.${breederAffiliation}`)}
          </Text>

          <View className={'mt-5 gap-3'}>
            <BreederProfileFact icon={falPaw} text={breederBreedsLabel} />

            {breederCity && <BreederProfileFact icon={falLocationDot} text={breederCity} />}

            <BreederProfileFact icon={falClock} text={breederResponseTimeText} />
          </View>

          {profileUsers.length > 0 && (
            <View className={'mt-6 gap-2'}>
              {profileUsers.map((user) => (
                <View
                  key={user.uuid}
                  className={'border-brand-brown-dark/10 rounded-lg border bg-white p-1.5'}
                >
                  <UserCard user={user} showRole={false} subtitle={t('common:breeder')} />
                </View>
              ))}
            </View>
          )}

          <View className={'mt-6 flex-row gap-3'}>
            <ContactBreederButton
              breeder={breeder}
              text={t('breeders.show_breeder.contact_us')}
              buttonClassName={'min-w-0 flex-1 max-sm:px-3'}
              textClassName={'whitespace-nowrap text-sm md:text-lg'}
            />

            <LikeButton
              testID={'breeder-profile-follow'}
              modelId={breeder.id}
              modelType={'App\\Models\\Breeder'}
              withText={true}
              dense={false}
              center={true}
              className={'min-w-0 flex-1 max-sm:px-3'}
              textClassName={'whitespace-nowrap text-sm md:text-lg'}
            />
          </View>
        </View>
      </View>

      <BreederProfileMenu breeder={breeder} />
    </View>
  );
}

function BreederProfileFact({
  icon,
  text,
  iconClassName = 'text-brand-brown-dark/70',
}: {
  icon: FaIcon;
  text: string;
  iconClassName?: string;
}) {
  return (
    <View className={'flex-row items-center gap-3'}>
      <Icon icon={icon} className={`${iconClassName} size-5`} />
      <Text className={'text-brand-brown-dark flex-1'}>{text}</Text>
    </View>
  );
}

function getBreederProfileImages(galleryAssets: MediaAsset[]) {
  return galleryAssets.map((asset) => asset.url).slice(0, 4);
}

function getBreederProfileThumbnailImages(images: string[]) {
  const thumbnailImages: Array<string | null> = [
    images[1] ?? images[0] ?? null,
    ...images.slice(2, 4),
  ];

  while (thumbnailImages.length < 3) {
    thumbnailImages.push(null);
  }

  return thumbnailImages;
}

function getBreederResponseTimeText(
  averageResponseTimeMinutes: number | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (!averageResponseTimeMinutes || averageResponseTimeMinutes < 1) {
    return t('breeders.show_breeder.response_time_fallback');
  }

  if (averageResponseTimeMinutes < 60) {
    return t('breeders.show_breeder.response_time_minutes', {
      count: Math.ceil(averageResponseTimeMinutes),
    });
  }

  const responseTimeHours = Math.ceil(averageResponseTimeMinutes / 60);

  if (responseTimeHours < 48) {
    return t('breeders.show_breeder.response_time_hours', {
      count: responseTimeHours,
    });
  }

  return t('breeders.show_breeder.response_time_days', {
    count: Math.ceil(responseTimeHours / 24),
  });
}

function BreederProfileMenu({ breeder }: { breeder: Breeder }) {
  const { t } = useTranslation('components');
  const route = useRoute();
  const isCurrentRoute = useIsCurrentRoute();
  const menuItems = [
    {
      title: t('components:breeder_menu.overview'),
      route: 'breeders.show',
    },
    {
      title:
        breeder.animal === Animal.Dog
          ? t('components:breeder_menu.our_dogs')
          : t('components:breeder_menu.our_cats'),
      route: 'breeders.show.pets',
    },
    {
      title: t('components:breeder_menu.our_litters'),
      route: 'breeders.show.litters',
    },
    {
      title: t('components:breeder_menu.photos'),
      route: 'breeders.show.photos',
    },
  ] as const;

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={false}
      className={'md:border-brand-brown-dark/10 mt-4 md:mt-0 md:border-t'}
      contentContainerClassName={
        'flex-row items-center md:grow md:justify-center md:gap-6 lg:gap-8'
      }
    >
      {menuItems.map((item) => {
        const selected = isCurrentRoute(item.route);

        return (
          <React.Fragment key={item.route}>
            <View className={'relative'}>
              <Link
                testID={`breeder-profile-tab-${item.route.split('.').pop()}`}
                href={route(item.route, { breeder: breeder.slug })}
                containerClassName={'group flex-row items-center px-3 py-3 md:px-6 md:py-4'}
                asBlock={true}
              >
                <Text
                  className={
                    'text-brand-brown-dark text-base font-semibold group-hover:underline md:text-lg'
                  }
                >
                  {item.title}
                </Text>
              </Link>

              {selected && (
                <View
                  className={
                    'bg-brand-green absolute bottom-0 left-4 right-4 h-0.5 md:left-6 md:right-6'
                  }
                />
              )}
            </View>
          </React.Fragment>
        );
      })}
    </ScrollView>
  );
}
